import { ArrowLeft, MousePointer2, LassoSelect, Pencil, Eraser, Type, Sticker, Image, Upload, Zap, RotateCcw, RotateCw, SlidersHorizontal, RotateCcw as ResetIcon, Orbit, Download, History, Cuboid, Loader2 } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useEditorUIStore } from '../../../stores';
import './KeyframeEditingSidebar.css';

// Tool definitions
const BASE_TOOLS = [
  { id: 'cursor', icon: MousePointer2, label: 'Select', shortcut: 'V' },
  { id: 'magic-select', icon: LassoSelect, label: 'Magic Select', shortcut: 'M' },
  { id: 'pen', icon: Pencil, label: 'Pen', shortcut: 'P' },
  { id: 'text', icon: Type, label: 'Text', shortcut: 'T' },
  { id: 'sticker', icon: Sticker, label: 'Sticker', shortcut: 'S' },
  { id: 'adjustments', icon: SlidersHorizontal, label: 'Adjustments', shortcut: 'A' },
  { id: 'camera', icon: Orbit, label: 'Camera Angle', shortcut: 'C' },
];

// Default color adjustments values
const DEFAULT_ADJUSTMENTS = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  tint: 0,
};

// Default camera angle values
const DEFAULT_CAMERA_ANGLE = {
  horizontal: 0,
  vertical: 0,
  zoom: 5,
};

function CameraOrbitControl({ 
  horizontalAngle = 0, 
  verticalAngle = 0, 
  zoom = 5,
  onChange,
  onApply,
  isGenerating = false,
}) {
  const hasChanges = horizontalAngle !== 0 || verticalAngle !== 0 || zoom !== 5;
  
  // Calculate camera position for visualization - image at center, camera orbits around
  const cameraViz = useMemo(() => {
    const CX = 90; // Center of visualization
    const CY = 70; // Center (image position) - moved down to show more space above
    
    // Convert angles to radians - 0° is front (viewer facing), clockwise rotation
    const yawRad = (horizontalAngle) * (Math.PI / 180);
    const pitchRad = verticalAngle * (Math.PI / 180);
    
    // Distance from center (zoom affects this - closer zoom = shorter distance)
    const baseDistance = 42;
    const distance = baseDistance + (10 - zoom) * 3;
    
    // 3D position: camera orbits around image
    // X: left (-) to right (+)
    // Z: front (+) to back (-)
    // Y: bottom (-) to top (+)
    const camX3D = Math.sin(yawRad) * Math.cos(pitchRad) * distance;
    const camZ3D = Math.cos(yawRad) * Math.cos(pitchRad) * distance; // Front is positive Z
    const camY3D = Math.sin(pitchRad) * distance; // Height above/below
    
    // Isometric-style projection for clearer 3D perception
    // Front (positive Z) appears at bottom, Back (negative Z) appears at top
    const isoFactor = 0.5; // Depth compression
    const camX = CX + camX3D;
    const camY = CY - camY3D * 0.8 + camZ3D * isoFactor; // + for correct depth: front=bottom, back=top
    
    // Camera scale: larger when in front (positive Z), smaller when behind
    const depthRatio = (camZ3D / distance + 1) / 2; // 0 = far back, 1 = far front
    const scale = 0.6 + depthRatio * 0.6; // Range: 0.6 to 1.2
    
    // Is camera in front of or behind the image?
    const isBehind = camZ3D < 0;
    
    // Calculate angle from camera to image center (for pointing direction)
    const rawAngle = Math.atan2(CY - camY, CX - camX) * (180 / Math.PI);
    
    // Prevent camera from flipping upside down when angle exceeds ±90°
    // If upside down, add 180° to rotation and mirror horizontally
    const isUpsideDown = rawAngle > 90 || rawAngle < -90;
    const angleToCenter = isUpsideDown ? rawAngle + 180 : rawAngle;
    const cameraFlipX = isUpsideDown ? -1 : 1; // Mirror horizontally to keep pointing at center
    
    // Calculate shadow position (projected onto ground plane)
    const shadowX = CX + camX3D;
    const shadowY = CY + camZ3D * isoFactor + 8; // Ground plane below center
    
    // Orbit ring Y positions at different X points (for 3D effect)
    const getOrbitY = (x, isFront) => {
      const xOffset = x - CX;
      const zAtX = Math.sqrt(Math.max(0, distance * distance - xOffset * xOffset));
      return CY + (isFront ? zAtX : -zAtX) * isoFactor; // + for correct depth
    };
    
    return { 
      x: camX, 
      y: camY, 
      cx: CX, 
      cy: CY, 
      distance, 
      scale, 
      isBehind, 
      angleToCenter,
      cameraFlipX,
      shadowX,
      shadowY,
      depthRatio,
      getOrbitY,
      camZ3D,
      camY3D
    };
  }, [horizontalAngle, verticalAngle, zoom]);
  
  // Generate orbit path points for 3D ellipse effect
  const orbitPath = useMemo(() => {
    const { cx, cy, distance, getOrbitY } = cameraViz;
    const points = [];
    const segments = 48;
    
    // Front half (visible, solid)
    const frontPoints = [];
    // Back half (behind, dashed)
    const backPoints = [];
    
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const x = cx + Math.sin(angle) * distance;
      const z = Math.cos(angle) * distance;
      const y = cy + z * 0.5; // + for correct depth: front=bottom, back=top
      
      if (z >= 0) {
        frontPoints.push({ x, y, z });
      } else {
        backPoints.push({ x, y, z });
      }
    }
    
    return { frontPoints, backPoints };
  }, [cameraViz]);
  
  // Create SVG path from points
  const pointsToPath = (points) => {
    if (points.length < 2) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  };
  
  return (
    <div className="keyframe-camera-orbit-wrapper">
      {/* Visualization */}
      <div className="keyframe-camera-viz">
        <svg viewBox="0 0 180 140" className="keyframe-camera-viz-svg">
          <defs>
            {/* Gradient for depth indication */}
            <linearGradient id="cameraVizGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--color-text-secondary)" stopOpacity="0.15" />
              <stop offset="100%" stopColor="var(--color-text-secondary)" stopOpacity="0.02" />
            </linearGradient>
            {/* Glow filter for camera */}
            <filter id="cameraGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            {/* Shadow filter */}
            <filter id="cameraShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.3 0" />
            </filter>
          </defs>
          
          {/* Ground plane indicator */}
          <ellipse 
            cx={cameraViz.cx} 
            cy={cameraViz.cy + 10} 
            rx={cameraViz.distance + 5} 
            ry={(cameraViz.distance + 5) * 0.35}
            fill="url(#cameraVizGradient)"
            className="keyframe-camera-viz-ground"
          />
          
          {/* Back orbit arc (behind image - dashed) */}
          <path
            d={pointsToPath(orbitPath.backPoints)}
            className="keyframe-camera-viz-orbit keyframe-camera-viz-orbit-back"
          />
          
          {/* Cardinal direction markers - Back/Front/Left/Right */}
          <g className="keyframe-camera-viz-labels">
            <text x={cameraViz.cx} y={cameraViz.cy - cameraViz.distance * 0.5 - 8} className="keyframe-camera-viz-label">Back</text>
            <text x={cameraViz.cx} y={cameraViz.cy + cameraViz.distance * 0.5 + 14} className="keyframe-camera-viz-label">Front</text>
            <text x={cameraViz.cx - cameraViz.distance - 8} y={cameraViz.cy + 4} className="keyframe-camera-viz-label">L</text>
            <text x={cameraViz.cx + cameraViz.distance + 8} y={cameraViz.cy + 4} className="keyframe-camera-viz-label">R</text>
          </g>
          
          {/* Connection line - behind (render first) */}
          {cameraViz.isBehind && (
            <line 
              x1={cameraViz.cx}
              y1={cameraViz.cy}
              x2={cameraViz.x}
              y2={cameraViz.y}
              className="keyframe-camera-viz-line keyframe-camera-viz-line-behind"
            />
          )}
          
          {/* Camera shadow (on ground) - behind */}
          {cameraViz.isBehind && (
            <ellipse
              cx={cameraViz.shadowX}
              cy={cameraViz.shadowY}
              rx={8 * cameraViz.scale}
              ry={3 * cameraViz.scale}
              className="keyframe-camera-viz-shadow"
              style={{ opacity: 0.15 * cameraViz.scale }}
            />
          )}
          
          {/* Camera - behind */}
          {cameraViz.isBehind && (
            <g 
              transform={`translate(${cameraViz.x}, ${cameraViz.y}) scale(${cameraViz.scale})`}
              className="keyframe-camera-viz-cam-group keyframe-camera-viz-cam-behind"
            >
              {/* Camera body */}
              <g transform={`rotate(${cameraViz.angleToCenter}) scale(${cameraViz.cameraFlipX}, 1)`}>
                {/* View cone / field of view */}
                <path 
                  d="M 6 0 L 24 -9 L 24 9 Z" 
                  className="keyframe-camera-viz-cone"
                  style={{ opacity: 0.15 }}
                />
                {/* Camera body - main housing */}
                <path 
                  d="M -14 -8 L -14 8 Q -14 10 -12 10 L 4 10 Q 6 10 6 8 L 6 -8 Q 6 -10 4 -10 L -12 -10 Q -14 -10 -14 -8 Z"
                  className="keyframe-camera-viz-cam-body"
                />
                {/* Grip - ergonomic bump on right side */}
                <path 
                  d="M -14 -6 Q -17 -5 -17 0 Q -17 5 -14 6"
                  className="keyframe-camera-viz-cam-grip"
                />
                {/* Viewfinder/EVF hump */}
                <path 
                  d="M -6 -10 L -6 -14 Q -6 -16 -4 -16 L 2 -16 Q 4 -16 4 -14 L 4 -10"
                  className="keyframe-camera-viz-cam-evf"
                />
                {/* Hot shoe mount */}
                <rect x="-3" y="-17" width="4" height="1.5" rx="0.5" className="keyframe-camera-viz-cam-hotshoe" />
                {/* Mode dial */}
                <circle cx="-10" cy="-12" r="2.5" className="keyframe-camera-viz-cam-dial" />
                {/* Shutter button */}
                <ellipse cx="-12" cy="-11" rx="1.5" ry="1" className="keyframe-camera-viz-cam-shutter" />
                {/* Lens mount ring */}
                <circle cx="0" cy="0" r="8" className="keyframe-camera-viz-lens-mount" />
                {/* Lens barrel outer */}
                <circle cx="2" cy="0" r="7" className="keyframe-camera-viz-lens-barrel" />
                {/* Focus ring */}
                <circle cx="3" cy="0" r="6" className="keyframe-camera-viz-lens-focus" />
                {/* Lens glass outer */}
                <circle cx="4" cy="0" r="5" className="keyframe-camera-viz-lens-outer" />
                {/* Lens glass inner */}
                <circle cx="4" cy="0" r="3.5" className="keyframe-camera-viz-lens-inner" />
                {/* Lens reflection/highlight */}
                <ellipse cx="2.5" cy="-1.5" rx="1.5" ry="1" className="keyframe-camera-viz-lens-highlight" />
              </g>
            </g>
          )}
          
          {/* Image plane (center) */}
          <g transform={`translate(${cameraViz.cx}, ${cameraViz.cy})`} className="keyframe-camera-viz-image-group">
            {/* Shadow under image */}
            <ellipse cx="0" cy="22" rx="16" ry="4" className="keyframe-camera-viz-image-shadow" />
            {/* Image frame */}
            <rect x="-14" y="-18" width="28" height="36" rx="3" className="keyframe-camera-viz-image" />
            {/* Image icon: mountains and sun */}
            <circle cx="5" cy="-6" r="3" className="keyframe-camera-viz-img-icon" />
            <path d="M -8 10 L -3 2 L 2 6 L 8 -2 L 8 10 Z" className="keyframe-camera-viz-img-icon-fill" />
          </g>
          
          {/* Front orbit arc (in front of image - solid) */}
          <path
            d={pointsToPath(orbitPath.frontPoints)}
            className="keyframe-camera-viz-orbit keyframe-camera-viz-orbit-front"
          />
          
          {/* Connection line - in front */}
          {!cameraViz.isBehind && (
            <line 
              x1={cameraViz.cx}
              y1={cameraViz.cy}
              x2={cameraViz.x}
              y2={cameraViz.y}
              className="keyframe-camera-viz-line"
            />
          )}
          
          {/* Camera shadow (on ground) - in front */}
          {!cameraViz.isBehind && (
            <ellipse
              cx={cameraViz.shadowX}
              cy={cameraViz.shadowY}
              rx={8 * cameraViz.scale}
              ry={3 * cameraViz.scale}
              className="keyframe-camera-viz-shadow"
              style={{ opacity: 0.2 * cameraViz.scale }}
            />
          )}
          
          {/* Camera - in front */}
          {!cameraViz.isBehind && (
            <g 
              transform={`translate(${cameraViz.x}, ${cameraViz.y}) scale(${cameraViz.scale})`}
              className="keyframe-camera-viz-cam-group"
              filter="url(#cameraGlow)"
            >
              {/* Camera body */}
              <g transform={`rotate(${cameraViz.angleToCenter}) scale(${cameraViz.cameraFlipX}, 1)`}>
                {/* View cone / field of view */}
                <path 
                  d="M 6 0 L 26 -11 L 26 11 Z" 
                  className="keyframe-camera-viz-cone"
                />
                {/* Camera body - main housing */}
                <path 
                  d="M -14 -8 L -14 8 Q -14 10 -12 10 L 4 10 Q 6 10 6 8 L 6 -8 Q 6 -10 4 -10 L -12 -10 Q -14 -10 -14 -8 Z"
                  className="keyframe-camera-viz-cam-body"
                />
                {/* Grip - ergonomic bump on right side */}
                <path 
                  d="M -14 -6 Q -17 -5 -17 0 Q -17 5 -14 6"
                  className="keyframe-camera-viz-cam-grip"
                />
                {/* Viewfinder/EVF hump */}
                <path 
                  d="M -6 -10 L -6 -14 Q -6 -16 -4 -16 L 2 -16 Q 4 -16 4 -14 L 4 -10"
                  className="keyframe-camera-viz-cam-evf"
                />
                {/* Hot shoe mount */}
                <rect x="-3" y="-17" width="4" height="1.5" rx="0.5" className="keyframe-camera-viz-cam-hotshoe" />
                {/* Mode dial */}
                <circle cx="-10" cy="-12" r="2.5" className="keyframe-camera-viz-cam-dial" />
                {/* Shutter button */}
                <ellipse cx="-12" cy="-11" rx="1.5" ry="1" className="keyframe-camera-viz-cam-shutter" />
                {/* Lens mount ring */}
                <circle cx="0" cy="0" r="8" className="keyframe-camera-viz-lens-mount" />
                {/* Lens barrel outer */}
                <circle cx="2" cy="0" r="7" className="keyframe-camera-viz-lens-barrel" />
                {/* Focus ring */}
                <circle cx="3" cy="0" r="6" className="keyframe-camera-viz-lens-focus" />
                {/* Lens glass outer */}
                <circle cx="4" cy="0" r="5" className="keyframe-camera-viz-lens-outer" />
                {/* Lens glass inner */}
                <circle cx="4" cy="0" r="3.5" className="keyframe-camera-viz-lens-inner" />
                {/* Lens reflection/highlight */}
                <ellipse cx="2.5" cy="-1.5" rx="1.5" ry="1" className="keyframe-camera-viz-lens-highlight" />
                {/* Recording indicator */}
                <circle cx="-1" cy="-14" r="1.5" className="keyframe-camera-viz-rec" />
              </g>
            </g>
          )}
          
          {/* Height indicator line (when camera is elevated) */}
          {Math.abs(verticalAngle) > 5 && (
            <g className="keyframe-camera-viz-height-indicator">
              <line
                x1={cameraViz.x}
                y1={cameraViz.y}
                x2={cameraViz.shadowX}
                y2={cameraViz.shadowY}
                className="keyframe-camera-viz-height-line"
              />
              <circle
                cx={cameraViz.shadowX}
                cy={cameraViz.shadowY}
                r="2"
                className="keyframe-camera-viz-height-dot"
              />
            </g>
          )}
        </svg>
      </div>
      
      {/* Rotation (Horizontal) */}
      <div className="keyframe-camera-slider-group">
        <div className="keyframe-camera-slider-header">
          <label className="keyframe-camera-slider-label">
            <span className="keyframe-camera-slider-dot keyframe-camera-dot-yaw" />
            Rotation
          </label>
          <span className="keyframe-camera-slider-value">{horizontalAngle}°</span>
        </div>
        <input
          type="range"
          min={0}
          max={360}
          value={horizontalAngle}
          onChange={(e) => onChange({ 
            horizontal: parseInt(e.target.value, 10), 
            vertical: verticalAngle, 
            zoom 
          })}
          className="keyframe-camera-slider keyframe-camera-slider-yaw"
          disabled={isGenerating}
        />
        <div className="keyframe-camera-slider-ticks">
          <span>0° Front</span>
          <span>90° Right</span>
          <span>180° Back</span>
          <span>270° Left</span>
        </div>
      </div>
      
      {/* Tilt (Vertical) */}
      <div className="keyframe-camera-slider-group">
        <div className="keyframe-camera-slider-header">
          <label className="keyframe-camera-slider-label">
            <span className="keyframe-camera-slider-dot keyframe-camera-dot-pitch" />
            Tilt
          </label>
          <span className="keyframe-camera-slider-value">{verticalAngle}°</span>
        </div>
        <input
          type="range"
          min={-30}
          max={90}
          value={verticalAngle}
          onChange={(e) => onChange({ 
            horizontal: horizontalAngle, 
            vertical: parseInt(e.target.value, 10), 
            zoom 
          })}
          className="keyframe-camera-slider keyframe-camera-slider-pitch"
          disabled={isGenerating}
        />
        <div className="keyframe-camera-slider-ticks">
          <span>-30° Low</span>
          <span>0° Eye</span>
          <span>45°</span>
          <span>90° Top</span>
        </div>
      </div>
      
      {/* Zoom (Distance) */}
      <div className="keyframe-camera-slider-group">
        <div className="keyframe-camera-slider-header">
          <label className="keyframe-camera-slider-label">
            <span className="keyframe-camera-slider-dot keyframe-camera-dot-dolly" />
            Zoom
          </label>
          <span className="keyframe-camera-slider-value">{zoom}</span>
        </div>
        <input
          type="range"
          min={0}
          max={10}
          value={zoom}
          onChange={(e) => onChange({ 
            horizontal: horizontalAngle, 
            vertical: verticalAngle, 
            zoom: parseInt(e.target.value, 10) 
          })}
          className="keyframe-camera-slider keyframe-camera-slider-dolly"
          disabled={isGenerating}
        />
        <div className="keyframe-camera-slider-ticks">
          <span>0 Wide</span>
          <span>5 Medium</span>
          <span>10 Close</span>
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="keyframe-camera-orbit-actions">
        <button
          className="keyframe-camera-orbit-reset"
          onClick={() => onChange(DEFAULT_CAMERA_ANGLE)}
          disabled={!hasChanges || isGenerating}
          title="Reset to default angle"
        >
          <ResetIcon size={14} />
          <span>Reset</span>
        </button>
        <button
          className="keyframe-camera-orbit-apply"
          onClick={onApply}
          disabled={!hasChanges || isGenerating}
          title="Apply camera angle change"
        >
          {isGenerating ? 'Generating...' : 'Apply'}
        </button>
      </div>
    </div>
  );
}

// Color Wheel Component - circular picker for temperature/tint
function ColorWheel({ temperature = 0, tint = 0, onChange }) {
  const wheelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Convert temperature/tint to position on wheel
  // Temperature: -100 (blue/cold) to +100 (orange/warm) maps to x-axis
  // Tint: -100 (green) to +100 (magenta) maps to y-axis
  const getIndicatorPosition = useCallback(() => {
    const x = (temperature / 100) * 40; // Max 40px from center
    const y = (tint / 100) * 40;
    return { x: 50 + x, y: 50 - y }; // Center at 50,50 (percentage)
  }, [temperature, tint]);
  
  const handleInteraction = useCallback((e) => {
    if (!wheelRef.current) return;
    
    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Get position relative to center
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left - centerX;
    const y = clientY - rect.top - centerY;
    
    // Clamp to circle radius
    const maxRadius = Math.min(centerX, centerY) * 0.8;
    const distance = Math.sqrt(x * x + y * y);
    const clampedDistance = Math.min(distance, maxRadius);
    const angle = Math.atan2(y, x);
    
    const clampedX = clampedDistance * Math.cos(angle);
    const clampedY = clampedDistance * Math.sin(angle);
    
    // Convert to temperature/tint values (-100 to +100)
    const newTemperature = Math.round((clampedX / maxRadius) * 100);
    const newTint = Math.round((-clampedY / maxRadius) * 100);
    
    onChange(newTemperature, newTint);
  }, [onChange]);
  
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    handleInteraction(e);
  }, [handleInteraction]);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleInteraction(e);
  }, [isDragging, handleInteraction]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  const indicatorPos = getIndicatorPosition();
  
  return (
    <div 
      className="keyframe-color-wheel"
      ref={wheelRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
    >
      <div className="keyframe-color-wheel-gradient" />
      <div className="keyframe-color-wheel-crosshair">
        <div className="keyframe-color-wheel-crosshair-h" />
        <div className="keyframe-color-wheel-crosshair-v" />
      </div>
      <div 
        className="keyframe-color-wheel-indicator"
        style={{ 
          left: `${indicatorPos.x}%`, 
          top: `${indicatorPos.y}%`,
        }}
      />
    </div>
  );
}

// Adjustment Slider Component
function AdjustmentSlider({ label, value, min, max, onChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef(null);
  
  const handleChange = useCallback((e) => {
    onChange(parseInt(e.target.value, 10));
  }, [onChange]);
  
  // Start editing when clicking on value
  const handleValueClick = useCallback(() => {
    setEditValue(String(value));
    setIsEditing(true);
  }, [value]);
  
  // Commit the edited value
  const handleEditCommit = useCallback(() => {
    const parsed = parseInt(editValue, 10);
    if (!isNaN(parsed)) {
      // Clamp to min/max range
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
    }
    setIsEditing(false);
  }, [editValue, min, max, onChange]);
  
  // Handle keyboard events in edit mode
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleEditCommit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  }, [handleEditCommit]);
  
  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  // Calculate fill percentage for visual feedback
  const fillPercent = ((value - min) / (max - min)) * 100;
  
  return (
    <div className="keyframe-adjustment-slider">
      <div className="keyframe-adjustment-slider-header">
        <label className="keyframe-adjustment-slider-label">{label}</label>
        {isEditing ? (
          <input
            ref={inputRef}
            type="number"
            className="keyframe-adjustment-slider-value-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditCommit}
            onKeyDown={handleKeyDown}
            min={min}
            max={max}
          />
        ) : (
          <button 
            className="keyframe-adjustment-slider-value"
            onClick={handleValueClick}
            title="Click to edit value"
          >
            {value > 0 ? `+${value}` : value}
          </button>
        )}
      </div>
      <div className="keyframe-adjustment-slider-track">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={handleChange}
          className="keyframe-adjustment-slider-input"
        />
        <div 
          className="keyframe-adjustment-slider-fill"
          style={{
            left: value >= 0 ? '50%' : `${fillPercent}%`,
            width: value >= 0 ? `${(value / max) * 50}%` : `${50 - fillPercent}%`,
          }}
        />
      </div>
    </div>
  );
}

function KeyframeEditingSidebar({
  title = 'Edit Keyframe',
  backLabel = 'Back to scene',
  saveTitle = 'Save current state as keyframe',
  exportTitle = 'Export current keyframe as PNG',
  enabledToolIds = null,
  keyframeUrl,
  sceneName,
  onBack,
  activeTool = 'cursor',
  onToolChange = () => {},
  onSave = () => {},
  isSaving = false,
  onExportImage = () => {},
  isExporting = false,
  penColor = '#ff0000',
  onPenColorChange = () => {},
  assets = [],
  onAssetSelect = () => {},
  onStartCreatingAsset = () => {},
  onStartUploadingAsset = () => {},
  // History props
  historyEntries = [],
  historyIndex = -1,
  onHistoryNavigate = () => {},
  onUndo = () => {},
  onRedo = () => {},
  // Color adjustments props
  colorAdjustments = DEFAULT_ADJUSTMENTS,
  onColorAdjustmentsChange = () => {},
  // Camera angle props (legacy)
  cameraAngle = DEFAULT_CAMERA_ANGLE,
  onCameraAngleChange = () => {},
  onCameraAngleApply = () => {},
  isCameraAngleGenerating = false,
  // World explorer props
  enableWorldExplorer = true,
  worldExplorerData = null,
  isWorldExploring = false,
  isWorldGenerating = false,
  isWorldCapturing = false,
  onGenerateWorld = () => {},
  onWorldCapture = () => {},
  onWorldExplorerCancel = () => {},
  keyframeVersions = [],
  activeVersionNumber = 0,
  onRestoreVersion = () => {},
  isRestoringVersion = false,
}) {
  const [createAssetDropdownOpen, setCreateAssetDropdownOpen] = useState(false);
  const previewingKeyframeVersionNumber = useEditorUIStore((s) => s.previewingKeyframeVersionNumber);
  const setPreviewingKeyframeVersionNumber = useEditorUIStore((s) => s.setPreviewingKeyframeVersionNumber);
  const selectedVersionNumber = previewingKeyframeVersionNumber ?? activeVersionNumber;
  const setSelectedVersionNumber = setPreviewingKeyframeVersionNumber;
  const hasPastVersions = keyframeVersions.length > 1;
  const tools = useMemo(() => {
    const enabled = enabledToolIds ? new Set(enabledToolIds) : null;
    const baseTools = enabled ? BASE_TOOLS.filter((tool) => enabled.has(tool.id)) : BASE_TOOLS;
    return hasPastVersions
      ? [...baseTools, { id: 'versions', icon: History, label: 'Versions' }]
      : baseTools;
  }, [enabledToolIds, hasPastVersions]);

  const handleUploadPhoto = () => {
    setCreateAssetDropdownOpen(false);
    onStartUploadingAsset();
  };

  const handleGenerateFromScratch = () => {
    setCreateAssetDropdownOpen(false);
    onStartCreatingAsset();
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < historyEntries.length - 1;
  const sortedVersions = Array.isArray(keyframeVersions) ? [...keyframeVersions].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0)) : [];
  const selectedVersion = sortedVersions.find((version) => version.versionNumber === selectedVersionNumber) || sortedVersions[sortedVersions.length - 1] || null;
  const isPreviewingPastVersion = activeTool === 'versions' && selectedVersion && selectedVersion.versionNumber !== activeVersionNumber;

  useEffect(() => {
    setPreviewingKeyframeVersionNumber(activeVersionNumber);
  }, [activeVersionNumber, keyframeVersions.length, setPreviewingKeyframeVersionNumber]);

  // Clean up preview state on unmount
  useEffect(() => {
    return () => setPreviewingKeyframeVersionNumber(null);
  }, [setPreviewingKeyframeVersionNumber]);

  useEffect(() => {
    const eraserIsAvailable = activeTool === 'eraser' && (!enabledToolIds || enabledToolIds.includes('pen') || enabledToolIds.includes('eraser'));
    if (!tools.some((tool) => tool.id === activeTool) && !eraserIsAvailable) {
      onToolChange('cursor');
    }
  }, [activeTool, enabledToolIds, onToolChange, tools]);

  const handleVersionKeyDown = useCallback((event, versionNumber) => {
    const currentIndex = sortedVersions.findIndex((version) => version.versionNumber === versionNumber);
    if (currentIndex < 0) return;
    if (event.key === 'ArrowDown' && currentIndex < sortedVersions.length - 1) {
      event.preventDefault();
      setSelectedVersionNumber(sortedVersions[currentIndex + 1].versionNumber);
    }
    if (event.key === 'ArrowUp' && currentIndex > 0) {
      event.preventDefault();
      setSelectedVersionNumber(sortedVersions[currentIndex - 1].versionNumber);
    }
  }, [sortedVersions]);

  return (
    <aside className="sidebar keyframe-editing-sidebar">
      {/* Header with back button */}
      <div className="sidebar-header config-header">
        <div className="header-content header-config-mode">
          <button
            className="config-back-button"
            onClick={onBack}
            aria-label={backLabel}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="sidebar-header-title-container">
            {typeof title === 'string'
              ? <span className="sidebar-header-title">{title}</span>
              : title}
          </div>
        </div>
      </div>

      <div className="keyframe-editing-content">
        {/* Tools Section */}
        <div className="keyframe-tools-section">
          <h3 className="keyframe-section-title">Tools</h3>
          <div
            className="keyframe-tools-grid"
            style={{ gridTemplateColumns: `repeat(${tools.length}, minmax(0, 1fr))` }}
          >
            {tools.map((tool) => {
              const Icon = tool.icon;
              const isActive = activeTool === tool.id;
              const isDisabled = tool.disabled;
              
              return (
                <button
                  key={tool.id}
                  className={`keyframe-tool-button ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && onToolChange(tool.id)}
                  disabled={isDisabled}
                  title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}${isDisabled ? ' - Coming soon' : ''}`}
                  aria-label={tool.label}
                  aria-pressed={isActive}
                >
                  <Icon size={16} />
                </button>
              );
            })}
          </div>
          
          {/* Color picker and eraser - only show for pen tool */}
          {activeTool === 'pen' && (
            <div className="keyframe-pen-options">
              <div className="keyframe-color-picker-wrapper">
                <label className="keyframe-color-label">Color</label>
                <input
                  type="color"
                  value={penColor}
                  onChange={(e) => onPenColorChange(e.target.value)}
                  className="keyframe-color-picker"
                  title="Pen color"
                />
              </div>
              <button
                className="keyframe-eraser-button"
                onClick={() => onToolChange('eraser')}
                title="Eraser (E)"
                aria-label="Eraser"
              >
                <Eraser size={16} />
              </button>
            </div>
          )}
          
          {/* Eraser mode indicator - show when eraser is active */}
          {activeTool === 'eraser' && (
            <div className="keyframe-eraser-active">
              <span className="keyframe-eraser-label">Eraser mode active</span>
              <button
                className="keyframe-back-to-pen-button"
                onClick={() => onToolChange('pen')}
                title="Back to Pen (P)"
              >
                <Pencil size={14} />
                <span>Back to Pen</span>
              </button>
            </div>
          )}
          
          {/* Assets grid - only show for sticker tool */}
          {activeTool === 'sticker' && (
            <div className="keyframe-sticker-assets-wrapper">
              <div className="keyframe-sticker-header">
                <label className="keyframe-sticker-label">Select an asset</label>
                <DropdownMenu.Root open={createAssetDropdownOpen} onOpenChange={setCreateAssetDropdownOpen}>
                  <DropdownMenu.Trigger asChild>
                    <button className="keyframe-create-asset-button" aria-label="Create an asset">
                      <span>Create asset</span>
                    </button>
                  </DropdownMenu.Trigger>
                  
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content className="keyframe-assets-dropdown-content" sideOffset={5} align="end">
                      <DropdownMenu.Item className="keyframe-assets-dropdown-item" onSelect={handleUploadPhoto}>
                        <Upload size={14} className="keyframe-assets-dropdown-icon" />
                        <span>Upload a photo</span>
                      </DropdownMenu.Item>
                      
                      <DropdownMenu.Item className="keyframe-assets-dropdown-item" onSelect={handleGenerateFromScratch}>
                        <Zap size={14} className="keyframe-assets-dropdown-icon" />
                        <span>Generate from scratch</span>
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
              {assets.length > 0 ? (
                <div className="keyframe-sticker-assets-grid">
                  {assets.map((asset) => {
                    const hasImage = asset.imageUrl && asset.imageUrl.length > 0;
                    return (
                      <div 
                        key={asset.id} 
                        className={`keyframe-sticker-asset-card ${!hasImage ? 'disabled' : ''}`}
                        onClick={() => hasImage && onAssetSelect(asset)}
                        title={hasImage ? `Add ${asset.name}` : 'Asset not ready'}
                      >
                        <div className="keyframe-sticker-asset-image-wrapper">
                          {hasImage ? (
                            <img 
                              src={asset.imageUrl} 
                              alt={asset.name}
                              className="keyframe-sticker-asset-image"
                            />
                          ) : (
                            <div className="keyframe-sticker-asset-placeholder">
                              <Image size={16} />
                            </div>
                          )}
                        </div>
                        <div className="keyframe-sticker-asset-name">{asset.name}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="keyframe-sticker-empty">
                  <p>No assets available</p>
                  <p className="keyframe-sticker-empty-hint">Create an asset to add it to your keyframe</p>
                </div>
              )}
            </div>
          )}
          
          {/* Color adjustments - only show for adjustments tool */}
          {activeTool === 'adjustments' && (
            <div className="keyframe-adjustments-wrapper">
              {/* Color Wheel */}
              <div className="keyframe-color-wheel-section">
                <label className="keyframe-adjustments-label">Color Tint</label>
                <ColorWheel
                  temperature={colorAdjustments.temperature}
                  tint={colorAdjustments.tint}
                  onChange={(temp, tintVal) => {
                    onColorAdjustmentsChange({
                      ...colorAdjustments,
                      temperature: temp,
                      tint: tintVal,
                    });
                  }}
                />
              </div>
              
              {/* Adjustment Sliders */}
              <div className="keyframe-adjustments-sliders">
                <AdjustmentSlider
                  label="Brightness"
                  value={colorAdjustments.brightness}
                  min={-100}
                  max={100}
                  onChange={(val) => onColorAdjustmentsChange({ ...colorAdjustments, brightness: val })}
                />
                <AdjustmentSlider
                  label="Contrast"
                  value={colorAdjustments.contrast}
                  min={-100}
                  max={100}
                  onChange={(val) => onColorAdjustmentsChange({ ...colorAdjustments, contrast: val })}
                />
                <AdjustmentSlider
                  label="Saturation"
                  value={colorAdjustments.saturation}
                  min={-100}
                  max={100}
                  onChange={(val) => onColorAdjustmentsChange({ ...colorAdjustments, saturation: val })}
                />
                <AdjustmentSlider
                  label="Temperature"
                  value={colorAdjustments.temperature}
                  min={-100}
                  max={100}
                  onChange={(val) => onColorAdjustmentsChange({ ...colorAdjustments, temperature: val })}
                />
                <AdjustmentSlider
                  label="Tint"
                  value={colorAdjustments.tint}
                  min={-100}
                  max={100}
                  onChange={(val) => onColorAdjustmentsChange({ ...colorAdjustments, tint: val })}
                />
              </div>
              
              {/* Reset Button */}
              <button
                className="keyframe-adjustments-reset"
                onClick={() => onColorAdjustmentsChange(DEFAULT_ADJUSTMENTS)}
                title="Reset all adjustments"
              >
                <ResetIcon size={14} />
                <span>Reset All</span>
              </button>
            </div>
          )}
          
          {activeTool === 'camera' && (
            <>
              {enableWorldExplorer && (
              <div className="keyframe-world-explorer-wrapper">
                <div className="keyframe-section-header">
                  <h3 className="keyframe-section-title">Advanced camera control</h3>
                </div>
                <p className="keyframe-world-explorer-desc">
                  Rebuild a navigable 3D scene from this frame, move freely, then capture the shot. Slower to start, richer spatial control.
                </p>

                {isWorldExploring ? (
                  <div className="keyframe-world-explorer-active">
                    <div className="keyframe-world-explorer-status">
                      <Cuboid size={16} />
                      <span>Exploring 3D scene</span>
                    </div>
                    <p className="keyframe-world-explorer-hint">
                      WASD to move, mouse to look, scroll to zoom. Click <strong>Capture angle</strong> when ready.
                    </p>
                    <div className="keyframe-world-explorer-actions">
                      <button
                        type="button"
                        className="keyframe-world-btn keyframe-world-btn-capture"
                        onClick={() => onWorldCapture()}
                        disabled={isWorldCapturing}
                      >
                        {isWorldCapturing ? 'Generating...' : 'Capture angle'}
                      </button>
                      <button
                        className="keyframe-world-btn keyframe-world-btn-cancel"
                        onClick={onWorldExplorerCancel}
                        disabled={isWorldCapturing}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="keyframe-world-explorer-idle">
                    <button
                      className="keyframe-world-btn keyframe-world-btn-generate"
                      onClick={onGenerateWorld}
                      disabled={isWorldGenerating}
                    >
                      {isWorldGenerating ? (
                        <>
                          <Loader2 size={16} className="keyframe-world-spinner" />
                          Preparing scene...
                        </>
                      ) : worldExplorerData?.spzUrl ? (
                        <>
                          <Cuboid size={16} />
                          Enter scene
                        </>
                      ) : (
                        <>
                          <Cuboid size={16} />
                          Prepare 3D scene
                        </>
                      )}
                    </button>
                    {isWorldGenerating && (
                      <p className="keyframe-world-explorer-hint">
                        First run is usually under a minute. This keyframe keeps the scene for quick re-entry.
                      </p>
                    )}
                  </div>
                )}
              </div>
              )}

              <div className="keyframe-camera-primary-section keyframe-camera-quick-section">
                <div className="keyframe-section-header">
                  <h3 className="keyframe-section-title">Quick angle</h3>
                </div>
                <p className="keyframe-camera-primary-desc">
                  Nudge rotation, tilt, and framing. Fast preview — best when you only need a small viewpoint shift.
                </p>
                <CameraOrbitControl
                  horizontalAngle={cameraAngle.horizontal}
                  verticalAngle={cameraAngle.vertical}
                  zoom={cameraAngle.zoom}
                  onChange={onCameraAngleChange}
                  onApply={onCameraAngleApply}
                  isGenerating={isCameraAngleGenerating}
                />
              </div>
            </>
          )}
        </div>

        {activeTool === 'versions' && hasPastVersions && (
        <div className="keyframe-versions-section">
          <div className="keyframe-history-header">
            <h3 className="keyframe-section-title">Versions</h3>
            {isPreviewingPastVersion && (
              <button
                className="keyframe-version-restore-btn"
                onClick={() => onRestoreVersion?.(selectedVersion?.versionNumber)}
                disabled={isRestoringVersion || !selectedVersion}
              >
                {isRestoringVersion ? 'Restoring…' : `Restore v${selectedVersion?.versionNumber}`}
              </button>
            )}
          </div>
          <div className="keyframe-version-list" role="listbox" aria-label="Keyframe versions">
            {sortedVersions.map((version) => {
              const isSelected = version.versionNumber === selectedVersionNumber;
              const isCurrentVersion = version.versionNumber === activeVersionNumber;
              return (
                <button
                  key={version.id}
                  className={`keyframe-version-item ${isSelected ? 'selected' : ''} ${isCurrentVersion ? 'current' : ''}`}
                  onClick={() => setSelectedVersionNumber(version.versionNumber)}
                  onKeyDown={(event) => handleVersionKeyDown(event, version.versionNumber)}
                >
                  <div className="keyframe-version-item-rail" />
                  <img
                    src={version.thumbnailUrl || version.keyframeUrl || keyframeUrl}
                    alt={`v${version.versionNumber}`}
                    className="keyframe-version-item-thumb"
                  />
                  <div className="keyframe-version-item-copy">
                    <span className="keyframe-version-item-label">{`v${version.versionNumber}`}</span>
                    <span className="keyframe-version-item-time">
                      {version.createdAt ? new Date(version.createdAt).toLocaleString() : 'Unknown time'}
                    </span>
                  </div>
                  {isCurrentVersion && <span className="keyframe-version-current-dot" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* History Section */}
        <div className="keyframe-history-section">
          <div className="keyframe-history-header">
            <h3 className="keyframe-section-title">History</h3>
            <div className="keyframe-history-actions">
              <button
                className="keyframe-history-action-btn"
                onClick={onUndo}
                disabled={!canUndo}
                title="Undo (Cmd+Z)"
                aria-label="Undo"
              >
                <RotateCcw size={14} />
              </button>
              <button
                className="keyframe-history-action-btn"
                onClick={onRedo}
                disabled={!canRedo}
                title="Redo (Cmd+Shift+Z)"
                aria-label="Redo"
              >
                <RotateCw size={14} />
              </button>
            </div>
          </div>
          <div className="keyframe-history-list">
            {historyEntries.length === 0 ? (
              <div className="keyframe-history-empty">
                <p>No history yet</p>
                <p className="keyframe-history-empty-hint">Make changes to see your edit history</p>
              </div>
            ) : (
              [...historyEntries].reverse().map((entry, reversedIdx) => {
                const actualIndex = historyEntries.length - 1 - reversedIdx;
                const isCurrent = actualIndex === historyIndex;
                const isFuture = actualIndex > historyIndex;
                
                return (
                  <button
                    key={entry.id}
                    className={`keyframe-history-item ${isCurrent ? 'current' : ''} ${isFuture ? 'future' : ''}`}
                    onClick={() => onHistoryNavigate(actualIndex)}
                    title={isFuture ? 'Click to redo to this state' : isCurrent ? 'Current state' : 'Click to undo to this state'}
                  >
                    <span className="keyframe-history-label">{entry.label}</span>
                    {isCurrent && <span className="keyframe-history-current-badge">Current</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="keyframe-save-bar">
        <button
          type="button"
          className="keyframe-export-button"
          onClick={onExportImage}
          disabled={isExporting}
          title={isExporting ? 'Exporting…' : exportTitle}
        >
          <Download size={14} />
          <span>{isExporting ? 'Exporting…' : 'Export Image'}</span>
        </button>
        <button
          type="button"
          className="keyframe-save-button"
          onClick={onSave}
          disabled={isSaving || isPreviewingPastVersion}
          title={isPreviewingPastVersion ? 'Restore or reselect the current version before saving' : isSaving ? 'Saving…' : saveTitle}
        >
          {isPreviewingPastVersion ? `Previewing v${selectedVersion?.versionNumber}` : isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </aside>
  );
}

export default KeyframeEditingSidebar;
